import React, { useEffect, useMemo, useState } from 'react';
import { Text, useGlobalization, StylovyzeDialog } from '@innovyze/stylovyze';
import {
	Checkbox,
	FormControl,
	FormControlLabel,
	FormGroup,
	FormLabel,
} from '@mui/material';

type DynamicConfigsPromptProps = {
	open: boolean;
	setOpenHandler: (state: boolean) => void;
	onConfirm: () => void;
	tenantName: string;
};

export const TitleCmp = (props: { title: string; tenantName: string }) => {
	const { title, tenantName } = props;
	return (
		<>
			<Text variant="body1" display="inline-flex">
				{title}
			</Text>
			<Text
				variant="body1"
				color="#aa0000"
				display="inline-flex"
				fontWeight="bold"
				marginLeft="8px">
				{tenantName}
			</Text>
		</>
	);
};

export const DynamicConfigsPrompt = (props: DynamicConfigsPromptProps) => {
	const { open, setOpenHandler, onConfirm, tenantName } = props;
	const { t } = useGlobalization();
	const [termsAccepted, setTermsAccepted] = useState<boolean>(false);

	useEffect(() => {
		setTermsAccepted(false);
	}, [open]);

	const TXT = useMemo(
		() => ({
			title: t('Generate Installation Code for'),
			msgA: t(
				'If this is the first installation, you can continue to generate and install.',
			),
			msgB: t(
				// eslint-disable-next-line max-len
				'If you already have a running Data Connector, re-generating the installation code will break it and you will need to re-install the Data Connector.',
			),
			btnConfirm: t('Generate'),
			btnCancel: t('Cancel'),
			checkBoxLabel: t(
				'I understand I must install or re-install the Data Connector',
			),
			checkBoxLegend: t('Are you sure you want to continue?'),
		}),
		[],
	);

	const handleOnClickCancelBtn = () => {
		setOpenHandler(false);
	};

	const handleOnClickConfirmBtn = () => {
		setOpenHandler(false);
		onConfirm();
	};

	const handleOnChangeCheckBox = () => {
		setTermsAccepted(!termsAccepted); // ? toggle
	};

	const renderReadCheckBox = () => {
		const identifier = 'warningRead';
		const dataCy = `${identifier}ConfirmCheckbox`;
		const dataTestId = 'warningDialogCheckBox';
		return (
			<FormControl component="fieldset">
				<FormLabel component="legend">{TXT.checkBoxLegend}</FormLabel>
				<FormGroup aria-label="position" row>
					<FormControlLabel
						value="end"
						control={
							<Checkbox
								id={identifier}
								name={identifier}
								data-cy={dataCy}
								checked={termsAccepted}
								onChange={handleOnChangeCheckBox}
								color="primary"
								data-testid={dataTestId}
							/>
						}
						label={TXT.checkBoxLabel}
						labelPlacement="end"
					/>
				</FormGroup>
			</FormControl>
		);
	};

	const titleCmp = useMemo(
		() => <TitleCmp title={TXT.title} tenantName={tenantName} />,
		[],
	);

	return (
		<StylovyzeDialog
			title={titleCmp}
			open={open}
			confirmText={TXT.btnConfirm}
			cancelText={TXT.btnCancel}
			onConfirm={handleOnClickConfirmBtn}
			onCancel={handleOnClickCancelBtn}
			confirmButtonProps={{ disabled: !termsAccepted }}
			dividers="both"
			dialogProps={{ 'data-testid': 'warningDialog' } as never}>
			<Text variant="body1">{TXT.msgA}</Text>
			<br />
			<Text variant="body1" style={{ maxWidth: '545px' }}>
				{TXT.msgB}
			</Text>
			<br />
			{renderReadCheckBox()}
		</StylovyzeDialog>
	);
};
